<template>
    <div
        v-bind="attrs"
        :tabindex="searchable ? -1 : tabindex"
        :class="{
            'multiselect--active': isOpen,
            'multiselect--disabled': disabled || loading || optionsLoading,
            'multiselect--above': isAbove,
        }"
        class="multiselect"
        @focus="activate()"
        @blur="searchable ? false : deactivate()"
        @keydown.self.down.prevent="pointerForward()"
        @keydown.self.up.prevent="pointerBackward()"
        @keypress.enter.tab.stop.self="addPointerElement($event)"
        @keyup.esc="deactivate()"
    >
        <slot name="clear" :search="search"></slot>
        <div
            ref="tags"
            class="multiselect__tags form-control h-auto"
            :class="{ 'is-valid': state === true, 'is-invalid': state === false }"
        >
            <slot name="caret" :toggle="toggle">
                <div class="multiselect__select" @mousedown.prevent.stop="toggle()"></div>
            </slot>
            <slot
                name="selection"
                :search="search"
                :remove="removeElement"
                :values="visibleValues"
                :is-open="isOpen"
            >
                <div v-if="!showCheckboxes" v-show="visibleValues.length > 0" class="multiselect__tags-wrap">
                    <template v-for="(option, index) in visibleValues">
                        <slot name="tag" :option="option" :search="search" :remove="removeElement">
                            <span :key="index" class="multiselect__tag">
                                <span v-text="getOptionLabel(option)"></span>
                                <i
                                    aria-hidden="true"
                                    tabindex="1"
                                    class="multiselect__tag-icon"
                                    @keypress.enter.prevent="removeElement(option); onCloseEmitted()"
                                    @mousedown.prevent="removeElement(option); onCloseEmitted()"
                                ></i>
                            </span>
                        </slot>
                    </template>
                </div>
                <template v-if="showCheckboxes && visibleValues.length && !isOpen">
                    {{ $t('selectField.selectedOptions') }} ({{ visibleValues.length }})
                </template>
                <template v-if="internalValue && internalValue.length > limit && !isOpen">
                    <slot name="limit">
                        <span v-text="limitText(internalValue.length - limit)"></span>
                    </slot>
                </template>
            </slot>
            <transition name="multiselect__loading">
                <slot name="loading">
                    <div v-show="loading || optionsLoading" class="multiselect__spinner"></div>
                </slot>
            </transition>
            <input
                v-if="searchable"
                :id="id"
                ref="search"
                :name="name"
                type="text"
                autocomplete="nope"
                :placeholder="placeholder"
                :style="inputStyle"
                :value="search"
                :disabled="disabled || loading || optionsLoading"
                :tabindex="tabindex"
                class="multiselect__input"
                @input="updateSearch($event.target.value)"
                @focus.prevent="onSearchFocus()"
                @blur.prevent="onSearchBlur()"
                @keyup.esc="deactivate()"
                @keydown.down.prevent="pointerForward()"
                @keydown.up.prevent="pointerBackward()"
                @keypress.enter.prevent.stop.self="addPointerElement($event)"
                @keydown.delete.stop="removeLastElement()"
            >
            <span v-if="isSingleLabelVisible" class="multiselect__single" @mousedown.prevent="toggle">
                <slot name="singleLabel" :option="singleValue">
                    {{ currentOptionLabel }}
                </slot>
            </span>
            <span
                v-if="isPlaceholderVisible"
                class="multiselect__placeholder nowrap"
                @mousedown.prevent="toggle"
            >
                <slot name="placeholder">
                    {{ placeholder }}
                </slot>
            </span>
        </div>
        <transition name="multiselect">
            <div
                v-show="isOpen"
                class="multiselect__content-container"
                :class="{
                    'multiselect__content-container--checkboxes': showCheckboxes,
                }"
            >
                <div
                    v-if="showCheckboxes"
                    class="multiselect__select-all multiselect__select-all--main"
                    @focus.stop="activate()"
                    @mousedown.prevent
                    @click="search ? toggleAllFiltered() : toggleAll()"
                >
                    <div>
                        <div class="custom-control custom-checkbox">
                            <input
                                :checked="checkedAllFiltered"
                                :indeterminate.prop="indeterminateAllFiltered"
                                :disabled="!filteredOptions.length"
                                type="checkbox"
                                class="custom-control-input"
                            >
                            <div class="custom-control-label text-white">
                                <slot name="selectAllHeader" :values="visibleValues">
                                    {{
                                        checkedAllFiltered
                                            ? $t('deselectAllVisible')
                                            : $t('selectAllVisible')
                                    }}
                                </slot>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="showCheckboxes" class="multiselect__search-inside">
                    <transition name="multiselect__loading">
                        <slot name="loadingInside">
                            <div
                                v-show="loading || isDebouncedOptionsLoading || isTyping"
                                class="multiselect__spinner"
                            ></div>
                        </slot>
                    </transition>
                    <feather type="search" class="multiselect__search-inside-icon text-muted flex-shrink-0" />
                    <input
                        v-if="searchable"
                        ref="searchInside"
                        type="text"
                        autocomplete="nope"
                        :placeholder="searchPlaceholder"
                        :style="inputStyle"
                        :value="search"
                        :disabled="disabled || loading || optionsLoading"
                        :tabindex="tabindex"
                        class="multiselect__input"
                        @input="updateSearch($event.target.value)"
                        @blur.prevent="deactivate()"
                    >
                    <transition name="fade">
                        <button
                            v-show="search"
                            type="button"
                            class="btn btn-default text-muted multiselect__search-inside-clear-btn"
                            @focus.stop="activate()"
                            @mousedown.prevent
                            @click="clearSearch"
                        >
                            <feather type="x" size="24" />
                        </button>
                    </transition>
                </div>
                <div
                    ref="list"
                    class="multiselect__content-wrapper"
                    tabindex="-1"
                    :style="{ maxHeight: optimizedHeight + 'px' }"
                    @focus="activate"
                    @mousedown.prevent
                >
                    <ul class="multiselect__content" :style="contentStyle">
                        <slot name="beforeList"></slot>
                        <li v-if="multiple && max === internalValue.length">
                            <span class="multiselect__option">
                                <slot name="maxElements">Maximum of {{ max }} options selected. First remove a selected
                                    option to select another.</slot>
                            </span>
                        </li>
                        <template v-if="!max || internalValue.length < max">
                            <template v-for="(partOptions, partOptionsIndex) in [filteredOptions, selectedOptionsShown]">
                                <li
                                    v-for="(option, index) in partOptions"
                                    :key="getItemKey(option)"
                                    class="multiselect__element"
                                    :class="{ 'multiselect__element-selected': partOptionsIndex === 1 }"
                                >
                                    <span
                                        v-if="!(option && (option.$isLabel || isOptionDisabled(option)))"
                                        :class="{
                                            ...optionHighlight(index, option),
                                            'multiselect__option__checkbox': showCheckboxes,
                                            'multiselect__option-selected': partOptionsIndex === 1,
                                            'multiselect__option--disabled': !!isOptionDisabled(option),
                                        }"
                                        :data-select="showCheckboxes || !allowEmpty ? '' : selectLabelText"
                                        :data-selected="showCheckboxes || !allowEmpty ? '' : selectedLabelText"
                                        :data-deselect="showCheckboxes || !allowEmpty ? '' : deselectLabelText"
                                        class="multiselect__option"
                                        @click.stop="select(option)"
                                        @mouseenter.self="pointerSet(index)"
                                    >
                                        <slot name="option" :option="option" :search="search">
                                            <div v-if="showCheckboxes" class="multiselect__checkbox-wrapper">
                                                <div class="custom-control custom-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        class="custom-control-input"
                                                        :checked="isSelected(option)"
                                                    >
                                                    <div class="custom-control-label">
                                                        <span :title="getOptionLabel(option)">{{ getOptionLabel(option) }}</span>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    class="btn btn-outline-primary btn-select-only"
                                                    @click.stop="selectOnly(option)"
                                                >
                                                    {{ $t('only') }}
                                                </button>
                                            </div>
                                            <span v-else>{{ getOptionLabel(option) }}</span>
                                        </slot>
                                    </span>
                                    <span
                                        v-if="option && (option.$isLabel || isOptionDisabled(option))"
                                        :data-select="showCheckboxes ? '' : groupSelect && selectGroupLabelText"
                                        :data-deselect="showCheckboxes ? '' : groupSelect && deselectGroupLabelText"
                                        :class="{
                                            ...groupHighlight(index, option),
                                            'multiselect__option__checkbox': showCheckboxes,
                                            'multiselect__option--disabled': !!isOptionDisabled(option),
                                        }"
                                        class="multiselect__option"
                                        @mouseenter.self="groupSelect && pointerSet(index)"
                                        @mousedown.prevent="selectGroup(option)"
                                    >
                                        <slot name="option" :option="option" :search="search">
                                            <div v-if="showCheckboxes" class="multiselect__checkbox-wrapper">
                                                <div class="custom-control custom-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        class="custom-control-input"
                                                        :checked="isSelected(option)"
                                                    >
                                                    <div class="custom-control-label">
                                                        <span :title="getOptionLabel(option)">{{ getOptionLabel(option) }}</span>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    class="btn btn-light btn-select-only"
                                                    @click.stop="selectOnly(option)"
                                                >
                                                    {{ $t('only') }}
                                                </button>
                                            </div>
                                            <span v-else>{{ getOptionLabel(option) }}</span>
                                        </slot>
                                    </span>
                                </li>
                                <template v-if="partOptionsIndex === 0">
                                    <li
                                        v-show="shouldShowNoOptions"
                                        :key="`noOptions-${partOptionsIndex}`"
                                    >
                                        <span class="multiselect__option multiselect__option--no-option">
                                            <slot name="noOptions">{{ noOptionsText }}</slot>
                                        </span>
                                    </li>
                                    <li
                                        v-show="shouldShowNoResults"
                                        :key="`noResult-${partOptionsIndex}`"
                                    >
                                        <span class="multiselect__option multiselect__option--no-result">
                                            <slot name="noResult" :search="search">{{ searchNotFoundText }}</slot>
                                        </span>
                                    </li>
                                    <li
                                        v-show="shouldShowMinCharWarning"
                                        :key="`minChar-${partOptionsIndex}`"
                                    >
                                        <span class="multiselect__option multiselect__option--min-char">
                                            <slot name="minChar" :search="search">
                                                <span v-text="minCharToSearchText(minCharToSearch)"></span>
                                            </slot>
                                        </span>
                                    </li>
                                    <li
                                        v-show="shouldShowDebounceLoading"
                                        :key="`debounceOptionsLoading-${partOptionsIndex}`"
                                    >
                                        <span class="multiselect__option multiselect__option--debounce-options-loading">
                                            <slot name="debounceOptionsLoading">
                                                {{ optionsLoadingText }}
                                                <div class="multiselect__spinner"></div>
                                            </slot>
                                        </span>
                                    </li>
                                    <li
                                        v-if="selectedOptionsShown.length"
                                        :key="`separator-${partOptionsIndex}`"
                                        class="multiselect__element multiselect__element--separator"
                                    >
                                        <div
                                            v-if="showCheckboxes"
                                            class="multiselect__select-all multiselect__select-all-selected rounded-0"
                                            @focus.stop="activate()"
                                            @mousedown.prevent
                                            @click="toggleAllSelected()"
                                        >
                                            <div>
                                                <div class="custom-control custom-checkbox">
                                                    <input
                                                        :checked="checkedAllSelected"
                                                        :indeterminate.prop="indeterminateAllSelected"
                                                        :disabled="!selectedOptionsShown.length"
                                                        type="checkbox"
                                                        class="custom-control-input"
                                                    >
                                                    <div class="custom-control-label">
                                                        <slot name="selectAllSelectedHeader" :values="visibleValues">
                                                            {{ selectedOptionsLabel }} ({{ selectedOptionsCount }})
                                                        </slot>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </template>
                            </template>
                        </template>
                        <slot name="afterList"></slot>
                    </ul>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
    /* eslint-disable no-nested-ternary */

    import Multiselect from 'vue-multiselect';
    import isEqual from 'lodash/isEqual';
    import sortBy from 'lodash/sortBy';
    import uniqBy from 'lodash/uniqBy';
    import debounce from 'lodash/debounce';
    import cloneDeep from 'lodash/cloneDeep';
    import { i18n } from '@/i18n/i18n';

    /**
     * Sprawdza, czy ciąg znaków zawiera zapytanie.
     * @param {String} string - Ciąg znaków do sprawdzenia.
     * @param {String} query - Zapytanie.
     * @returns {Boolean} - True, jeśli zawiera zapytanie.
     */
    function includes(string, query) {
        const str = string ?? '';
        const text = str.toString().toLowerCase();
        return text.includes(query.trim());
    }

    /**
     * Filtruje opcje na podstawie wyszukiwania.
     * @param {Array} options - Lista opcji.
     * @param {String} search - Wyszukiwana fraza.
     * @param {String} label - Klucz etykiety.
     * @param {Function} customLabel - Funkcja zwracająca etykietę.
     * @returns {Array} - Przefiltrowane opcje.
     */
    function filterOptions(options, search, label, customLabel) {
        return options.filter(option => includes(customLabel(option, label), search));
    }

    /**
     * Spłaszcza zagnieżdżone opcje.
     * @param {String} values - Klucz wartości.
     * @param {String} label - Klucz etykiety.
     * @returns {Function} - Funkcja przetwarzająca opcje.
     */
    function flattenOptions(values, label) {
        return options =>
            options.reduce((prev, curr) => {
                if (curr[values] && curr[values].length) {
                    prev.push({
                        $groupLabel: curr[label],
                        $isLabel: true,
                    });
                    return prev.concat(curr[values]);
                }
                return prev;
            }, []);
    }

    /**
     * Sprawdza, czy wartość jest pusta.
     * @param {any} opt - Wartość do sprawdzenia.
     * @returns {Boolean} - True, jeśli wartość jest pusta.
     */
    function isEmpty(opt) {
        if (opt === false) return false;
        if (opt === 0) return false;
        if (Array.isArray(opt) && opt.length === 0) return true;
        return !opt;
    }

    export default {
        name: 'Multiselect',
        extends: Multiselect,
        inheritAttrs: false,
        props: {
            options: {
                type: Array,
                default: () => [],
            },
            showCheckboxes: {
                type: Boolean,
                default: false,
            },
            trackBy: {
                type: String,
                default: 'value',
            },
            trackByLabel: {
                type: String,
                default: 'value',
            },
            label: {
                type: String,
                default: 'text',
            },
            placeholder: {
                type: String,
                default: () => i18n.t('selectOption'),
            },
            searchPlaceholder: {
                type: String,
                default: () => i18n.t('selectField.searchPlaceholder'),
            },
            selectLabel: {
                type: String,
                default: '',
            },
            selectGroupLabel: {
                type: String,
                default: () => i18n.t('selectField.selectGroupLabel'),
            },
            selectedLabel: {
                type: String,
                default: () => i18n.t('selectField.selectedLabel'),
            },
            deselectLabel: {
                type: String,
                default: () => i18n.t('selectField.deselectLabel'),
            },
            deselectGroupLabel: {
                type: String,
                default: () => i18n.t('selectField.deselectGroupLabel'),
            },
            limitText: {
                type: Function,
                default: count => i18n.t('selectField.limitText', { count }),
            },
            searchNotFoundText: {
                type: String,
                default: () => i18n.t('selectField.searchNotFoundText'),
            },
            noOptionsText: {
                type: String,
                default: () => i18n.t('selectField.noOptionsText'),
            },
            selectedOptionsLabel: {
                type: String,
                default: () => i18n.t('selectField.previouslySelectedOptions'),
            },
            showSelected: {
                type: Boolean,
                default: false,
            },
            asynchronous: {
                type: Boolean,
                default: false,
            },
            fetchOptionsFunction: {
                type: Function,
                default: null,
            },
            showSelectedOnTop: {
                type: Boolean,
                default: false,
            },
            parseOptionsFunction: {
                type: Function,
                default: null,
            },
            debounceDelay: {
                type: Number,
                default: 750,
            },
            minCharToSearch: {
                type: Number,
                default: 3,
                validator: value => value > 0,
            },
            minCharToSearchText: {
                type: Function,
                default: min => i18n.t('selectField.minCharToSearchText', { min }),
            },
            preselectAll: {
                type: Boolean,
                default: false,
            },
            state: {
                type: Boolean,
                default: null,
                validator: value => [null, false, true].includes(value),
            },
            optionsLoadingText: {
                type: String,
                default: () => i18n.t('selectField.optionsLoadingText'),
            },
            initialValue: undefined,
            customLabel: {
                type: Function,
                default(option, label) {
                    if (isEmpty(option)) return '';
                    if (typeof option === 'number' || typeof option === 'string') return option;
                    return label && option[label] ? option[label] : '';
                },
            },
            disabledOptions: {
                type: Array,
                default: () => [],
            },
        },
        data() {
            return {
                modelValue: this.multiple ? [] : null,
                emittedInputData: null,
                debouncedFetchOptions: null,
                pendingFetchOptionsPromises: [],
                isDebouncedOptionsLoading: false,
                loadedOptions: [],
                loadingOptionsComplete: true,
                internalSelectedOptions: [],
                isTyping: false,
                optionsLoading: false,
                valueOnOpen: null,
            };
        },
        computed: {
            attrs() {
                return {
                    ...this.$attrs,
                    id: this.id ? `multiselect-${this.id}` : undefined,
                };
            },
            hasFetchOptionsFunction() {
                return typeof this.fetchOptionsFunction === 'function';
            },
            internalValue() {
                if (Array.isArray(this.modelValue)) {
                    return this.modelValue;
                }
                if (this.modelValue != null) {
                    return [this.modelValue];
                }
                return [];
            },
            checkedAll() {
                return this.filteredOptions.length === this.internalValue.length;
            },
            indeterminateAll() {
                return !this.checkedAll && this.internalValue.length > 0;
            },
            checkedAllFiltered() {
                return this.filteredOptions.length > 0 && this.filteredOptions.every(option => this.isSelected(option));
            },
            checkedAllSelected() {
                return this.selectedOptionsShown.length > 0 && this.selectedOptionsShown.every(option => this.isSelected(option));
            },
            indeterminateAllFiltered() {
                const anyOfFilteredIsSelected = this.filteredOptions.some(option => this.isSelected(option));
                return !this.checkedAllFiltered && anyOfFilteredIsSelected;
            },
            indeterminateAllSelected() {
                const anyOfSelectedIsSelected = this.selectedOptionsShown.some(option => this.isSelected(option));
                return !this.checkedAllSelected && anyOfSelectedIsSelected;
            },
            contentStyle() {
                return { display: 'block' };
            },
            optionsValues() {
                return this.filteredOptions.map(this.getItemValue);
            },
            selectedOptions() {
                const optionsValues = this.optionsValues;
                const value = Array.isArray(this.value) ? this.value : [];
                const uniqueValues = uniqBy([...this.internalSelectedOptions, ...this.internalValue, ...value], this.trackBy);
                const filteredValues = uniqueValues.filter(item => !optionsValues.includes(this.getItemValue(item)));
                return sortBy(filteredValues, this.label);
            },
            selectedOptionsShown() {
                return this.showSelected ? this.selectedOptions : [];
            },
            selectedOptionsCount() {
                return this.selectedOptionsShown.length;
            },
            optionsFilteredPlusSelected() {
                return [...this.filteredOptions, ...this.selectedOptionsShown];
            },
            filteredOptions() {
                let options = [...this.options];

                if (this.asynchronous && this.loadedOptions.length) {
                    options = this.loadedOptions;
                }

                if (!this.asynchronous && this.hasFetchOptionsFunction) {
                    options = this.loadedOptions;
                }

                const search = this.search || '';
                const normalizedSearch = search.toLowerCase().trim();

                if (this.internalSearch) {
                    options = this.groupValues ? this.filterAndFlat(options, normalizedSearch, this.label) : filterOptions(options, normalizedSearch, this.label, this.customLabel);
                } else {
                    options = this.groupValues ? flattenOptions(this.groupValues, this.groupLabel)(options) : options;
                }

                if (this.taggable && normalizedSearch.length && !this.isExistingOption(normalizedSearch)) {
                    const tagOption = { isTag: true, label: search };
                    if (this.tagPosition === 'bottom') {
                        options.push(tagOption);
                    } else {
                        options.unshift(tagOption);
                    }
                }

                if (this.showSelectedOnTop && this.valueOnOpen) {
                    options = options.sort((a, b) => {
                        const aWasSelectedOnOpen = this.valueOnOpen.some(item => this.getItemValue(item) === this.getItemValue(a));
                        const bWasSelectedOnOpen = this.valueOnOpen.some(item => this.getItemValue(item) === this.getItemValue(b));

                        // Only move items that were already selected when the dropdown opened
                        if (aWasSelectedOnOpen && !bWasSelectedOnOpen) return -1;
                        if (!aWasSelectedOnOpen && bWasSelectedOnOpen) return 1;
                        return 0;
                    });
                }

                return options;
            },
            disabledIds() {
                if (!this.disabledOptions) return [];
                if (typeof this.disabledOptions[0] === 'object') {
                    return this.disabledOptions.map(item => item[this.trackBy]);
                }
                return this.disabledOptions;
            },
            shouldShowNoOptions() {
                return this.showNoOptions && this.filteredOptions.length === 0 && !this.search && !this.loading;
            },
            shouldShowNoResults() {
                return this.showNoResults && this.filteredOptions.length === 0 && this.search.length >= this.minCharToSearch && !this.loading && this.loadingOptionsComplete;
            },
            shouldShowMinCharWarning() {
                return this.asynchronous && this.search && this.search.length < this.minCharToSearch;
            },
            shouldShowDebounceLoading() {
                return this.asynchronous && this.isTyping && !this.showCheckboxes;
            },
        },
        watch: {
            value: {
                handler(newVal, oldVal) {
                    if (isEmpty(oldVal) && isEmpty(newVal)) {
                        return;
                    }
                    this.initModelValue(this.value);
                    this.internalSelectedOptions = [];
                },
                immediate: true,
            },
            initialValue: {
                handler(newVal, oldVal) {
                    if (isEmpty(oldVal) && isEmpty(newVal)) {
                        return;
                    }
                    this.handleInitialValueChange(newVal, oldVal);
                },
                immediate: true,
            },
            options: {
                handler() {
                    if (isEmpty(this.options) || isEmpty(this.initialValue)) {
                        return;
                    }
                    this.handleInitialValueChange(this.initialValue, null);
                },
                immediate: false,
            },
            isOpen() {
                if (!this.isOpen && this.showSelected) {
                    this.internalSelectedOptions = [...this.internalValue];
                    this.$nextTick(() => {
                        if (this.$refs.list) {
                            this.$refs.list.scrollTop = 0;
                        }
                    });
                }
            },
            search() {
                this.handleSearchChange();
            },
            pendingFetchOptionsPromises(promises) {
                this.handlePendingFetchOptionsPromises(promises);
            },
            isDebouncedOptionsLoading(loading) {
                this.$emit('loading', loading);
                if (!loading) this.loadingOptionsComplete = true;
            },
            loadingOptionsComplete(loading) {
                if (loading) this.isTyping = false;
            },
        },
        methods: {
            /**
             * Zwraca unikalną wartość jako klucz
             */
            getItemKey(option) {
                if (typeof option === 'object') return option[this.trackBy] || option.id || option.value;
                return option;
            },
            /**
             * Obsługuje zmianę wartości wyszukiwania.
             */
            handleSearchChange() {
                if (this.asynchronous) {
                    this.loadedOptions = [];
                    this.loadingOptionsComplete = false;
                    this.isTyping = false;
                    if (this.search.length >= this.minCharToSearch) {
                        this.isTyping = true;
                        this.debouncedFetchOptions?.();
                    } else {
                        this.pendingFetchOptionsPromises = [];
                        this.isDebouncedOptionsLoading = false;
                    }
                }
                if (this.showSelected) {
                    this.internalSelectedOptions = uniqBy([...this.internalSelectedOptions, ...this.internalValue], this.trackBy);
                    this.$nextTick(() => {
                        if (this.$refs.list) {
                            this.$refs.list.scrollTop = 0;
                        }
                    });
                }
            },
            /**
             * Obsługuje zmiany w liście oczekujących obietnic pobierania opcji.
             * @param {Array} promises - Lista obietnic.
             */
            handlePendingFetchOptionsPromises(promises) {
                const promisesLength = promises.length;
                if (promisesLength) {
                    this.isDebouncedOptionsLoading = true;
                    Promise.all(promises)
                        .then(values => {
                            if (promisesLength === this.pendingFetchOptionsPromises.length && this.search) {
                                const lastResponse = values[values.length - 1];
                                this.loadedOptions = this.parseOptionsFunction ? this.parseOptionsFunction(lastResponse) : lastResponse || [];
                            }
                        })
                        .catch(error => {
                            if (promisesLength === this.pendingFetchOptionsPromises.length && this.search) {
                                this.loadedOptions = [];
                            }
                        })
                        .finally(() => {
                            if (promisesLength === this.pendingFetchOptionsPromises.length && this.search) {
                                this.pendingFetchOptionsPromises = [];
                                this.isDebouncedOptionsLoading = false;
                            }
                        });
                }
            },
            /**
             * Obsługuje zmianę wartości początkowej.
             * @param {any} newVal - Nowa wartość.
             * @param {any} oldVal - Stara wartość.
             */
            handleInitialValueChange(newVal, oldVal) {
                if (!this.multiple) {
                    if (this.modelValue) {
                        if (this.trackBy) {
                            if (this.initialValue === this.modelValue[this.trackBy]) return;
                        } else if (this.initialValue === this.modelValue) return;
                    }
                }

                const isChanged = JSON.stringify(newVal) !== JSON.stringify(oldVal) && JSON.stringify(newVal) !== JSON.stringify(this.modelValue);
                if (!isChanged || !(this.hasFetchOptionsFunction || this.options?.length)) {
                    return;
                }

                const modelValue = !Array.isArray(this.modelValue) && this.multiple ? [this.modelValue] : this.modelValue;
                const modelValueKeys = this.trackBy && this.multiple ? modelValue.map(item => item[this.trackBy]) : modelValue;
                const newInitialValue = Array.isArray(newVal) ? sortBy(newVal) : this.multiple ? [newVal] : newVal;

                if (JSON.stringify(modelValueKeys) === JSON.stringify(newInitialValue)) {
                    return;
                }

                const processedInitialValue = this.processInitialValue(this.initialValue);

                if (this.trackBy) {
                    this.modelValue = this.multiple ? processedInitialValue : processedInitialValue[0] || null;
                } else if (this.multiple) {
                    this.modelValue = Array.isArray(this.initialValue) ? [...this.initialValue] : [this.initialValue];
                } else {
                    this.modelValue = this.initialValue === null || this.initialValue === undefined ? null : this.initialValue;
                }
            },
            /**
             * Przetwarza wartość początkową na odpowiedni format.
             * @param {any} initialValue - Wartość początkowa.
             * @returns {Array} - Przetworzona wartość początkowa.
             */
            processInitialValue(initialValue) {
                const fnMap = item => {
                    if (item && typeof item === 'object') return item;
                    return {
                        [this.trackBy]: item,
                        [this.label]: this.getOptionLabel(item),
                        isInitial: true,
                    };
                };
                if (Array.isArray(initialValue)) {
                    return initialValue.map(fnMap);
                }
                return [fnMap(initialValue)];
            },
            /**
             * Zwraca etykietę dla opcji.
             * @param {Object} option - Opcja.
             * @returns {String} - Etykieta opcji.
             */
            getOptionLabel(option) {
                if (isEmpty(option)) return '';
                if (option.isTag) return option.label;
                if (option.$isLabel) return option.$groupLabel;
                if (option.isInitial && option[this.trackBy] === option[this.label] && this.trackByLabel) {
                    return `${this.trackByLabel}: ${option[this.label]}`;
                }
                const label = this.customLabel(option, this.label);
                if (isEmpty(label)) return '';
                return label;
            },
            /**
             * Aktualizuje etykietę w wartości początkowej, jeśli jest to konieczne.
             * @param {Object} option - Opcja.
             * @param {Boolean} isSelected - Czy opcja jest zaznaczona.
             * @param {Number} index - Indeks opcji.
             */
            updateLabelInInitialValue(option, isSelected, index) {
                const modelValueItem = this.multiple ? this.modelValue?.[index] : this.modelValue;
                if (this.initialValue && modelValueItem?.isInitial && isSelected && this.trackBy && !option.isInitial && modelValueItem?.[this.trackBy] === option[this.trackBy] && modelValueItem?.[this.label] !== option[this.label]) {
                    if (this.label) {
                        this.$set(modelValueItem, [this.label], this.getOptionLabel(option));
                    } else {
                        Object.keys(option).forEach(key => {
                            this.$set(modelValueItem, key, option[key]);
                        });
                    }
                    this.$set(modelValueItem, 'isInitial', false);
                }
            },
            /**
             * Sprawdza, czy opcja jest zaznaczona.
             * @param {Object} option - Opcja.
             * @returns {Boolean} - Czy opcja jest zaznaczona.
             */
            isSelected(option) {
                const optionValue = this.getItemValue(option);
                const isSelected = this.valueKeys.includes(optionValue);
                if (isSelected) {
                    const index = this.valueKeys.indexOf(optionValue);
                    this.updateLabelInInitialValue(option, isSelected, index);
                }
                return isSelected;
            },
            /**
             * Wybiera opcję.
             * @param {Object} option - Opcja.
             * @param {String} key - Klawisz.
             */
            select(option, key) {
                if (option.$isLabel && this.groupSelect) {
                    this.selectGroup(option);
                    return;
                }
                if (this.isSelectionBlocked(option, key)) return;

                if (key === 'Tab' && !this.pointerDirty) return;

                if (option.isTag) {
                    this.handleTagSelection(option);
                } else {
                    this.handleOptionSelection(option);
                }

                if (this.closeOnSelect) this.deactivate();
            },
            /**
             * Sprawdza, czy wybór opcji jest zablokowany.
             * @param {Object} option - Opcja.
             * @param {String} key - Klawisz.
             * @returns {Boolean} - Czy wybór jest zablokowany.
             */
            isSelectionBlocked(option, key) {
                return this.blockKeys.includes(key) || this.disabled || this.isOptionDisabled(option) || option.$isLabel || (this.max && this.multiple && this.internalValue.length === this.max);
            },
            /**
             * Obsługuje wybór tagu.
             * @param {Object} option - Opcja.
             */
            handleTagSelection(option) {
                this.$emit('tag', option.label, this.id);
                this.search = '';
                if (this.closeOnSelect && !this.multiple) this.deactivate();
            },
            /**
             * Obsługuje wybór opcji.
             * @param {Object} option - Opcja.
             */
            handleOptionSelection(option) {
                const isSelected = this.isSelected(option);

                if (isSelected) {
                    this.removeElement(option);
                    return;
                }

                this.$emit('select', option, this.id);

                const val = this.multiple ? [...this.internalValue, option] : option;
                if (this.showCheckboxes && this.asynchronous) {
                    this.modelValue = val;
                } else {
                    this.$emit('input', val, this.id);
                }

                if (this.clearOnSelect) this.search = '';
            },
            /**
             * Usuwa element z listy wybranych.
             * @param {Object} option - Opcja.
             * @param {Boolean} shouldClose - Czy zamknąć po usunięciu.
             */
            removeElement(option, shouldClose = true) {
                if (this.disabled) return;
                if (this.isOptionDisabled(option)) return;
                if (!this.allowEmpty && this.internalValue.length <= 1) {
                    this.deactivate();
                    return;
                }

                const index = typeof option === 'object' ? this.valueKeys.indexOf(option[this.trackBy]) : this.valueKeys.indexOf(option);

                this.$emit('remove', option, this.id);
                if (this.multiple) {
                    const newValue = this.internalValue.slice(0, index).concat(this.internalValue.slice(index + 1));
                    this.$emit('input', newValue, this.id);
                } else {
                    this.$emit('input', null, this.id);
                }

                if (this.closeOnSelect && shouldClose) this.deactivate();
            },
            onSearchFocus() {
                this.activate();
                if (this.showCheckboxes) this.$refs.searchInside.focus();
            },
            onSearchBlur() {
                if (!this.showCheckboxes) this.deactivate();
            },
            /**
             * Przełącza zaznaczenie wszystkich opcji.
             */
            toggleAll() {
                const enabledOptions = this.filteredOptions.filter(option => !this.isOptionDisabled(option));
                // Jeśli wszystkie są zaznaczone, odznaczamy wszystkie
                if (this.checkedAll) {
                    const remainingOptions = this.internalValue.filter(item => {
                        if (this.trackBy && typeof item === 'object') {
                            return !enabledOptions.map(op => op[this.trackBy]).includes(item[this.trackBy]);
                        }
                        return !enabledOptions.includes(item);
                    });
                    this.$emit('input', remainingOptions, this.id);
                } else {
                    // Zaznaczamy wszystkie nowe opcje, nie zmieniając już zaznaczonych
                    const newOptions = enabledOptions.filter(option => !this.isSelected(option));
                    this.$emit('input', [...this.internalValue, ...newOptions], this.id);
                }
            },
            /**
             * Przełącza zaznaczenie wszystkich filtrowanych opcji.
             */
            toggleAllFiltered() {
                const enabledFilteredOptions = this.filteredOptions.filter(option => !this.isOptionDisabled(option));
                // Jeśli wszystkie filtrowane są zaznaczone, odznaczamy tylko filtrowane
                if (this.checkedAllFiltered) {
                    const remainingOptions = this.internalValue.filter(item => {
                        if (this.trackBy && typeof item === 'object') {
                            return !enabledFilteredOptions.map(op => op[this.trackBy]).includes(item[this.trackBy]);
                        }
                        return !enabledFilteredOptions.includes(item);
                    });
                    this.$emit('input', remainingOptions, this.id);
                } else {
                    // Zaznaczamy wszystkie nowe filtrowane opcje, nie zmieniając już zaznaczonych
                    const newOptions = enabledFilteredOptions.filter(option => !this.isSelected(option));
                    this.$emit('input', [...this.internalValue, ...newOptions], this.id);
                }
            },
            /**
             * Przełącza zaznaczenie wszystkich wybranych opcji.
             */
            toggleAllSelected() {
                const enabledSelectedOptions = this.selectedOptionsShown.filter(option => !this.isOptionDisabled(option));
                if (this.checkedAllSelected) {
                    const remainingOptions = this.internalValue.filter(item => {
                        if (this.trackBy && typeof item === 'object') {
                            return !enabledSelectedOptions.map(op => op[this.trackBy]).includes(item[this.trackBy]);
                        }
                        return !enabledSelectedOptions.includes(item);
                    });
                    this.$emit('input', remainingOptions, this.id);
                } else {
                    const newOptions = enabledSelectedOptions.filter(option => !this.isSelected(option));
                    this.$emit('input', [...this.internalValue, ...newOptions], this.id);
                }
            },
            /**
             * Wybiera tylko jedną opcję.
             * @param {Object} option - Opcja.
             */
            selectOnly(option) {
                this.$emit('input', [option], this.id);
            },
            clearSearch() {
                this.search = '';
            },
            emitTrackByEvent(value, eventName) {
                if (this.trackBy) {
                    if (value === null) {
                        this.$emit(eventName, value);
                    } else if (typeof value === 'object') {
                        if (Array.isArray(value)) {
                            this.$emit(
                                eventName,
                                value.map(item => item[this.trackBy])
                            );
                        } else {
                            this.$emit(eventName, value[this.trackBy]);
                        }
                    }
                }
            },
            onInputEmitted(value, id) {
                this.modelValue = value;
                this.emitTrackByEvent(value, 'inputTrackBy');
            },
            onOpenEmitted() {
                this.valueOnOpen = cloneDeep(this.modelValue);
            },
            onCloseEmitted(value, id) {
                const sortKey = typeof this.value?.[0] === 'object' ? this.label : null;
                // const changed1 = !isEqual(sortBy(this.value, sortKey), sortBy(this.modelValue, sortKey));
                // const changed2 = !isEqual(sortBy(this.value, sortKey), sortBy(this.valueOnOpen, sortKey));
                const changed3 = !isEqual(sortBy(this.modelValue, sortKey), sortBy(this.valueOnOpen, sortKey));
                if (changed3) {
                    this.$emit('input', this.modelValue, id);
                    this.emitChange();
                }
                this.valueOnOpen = null;
            },
            initAsynchronous() {
                if (this.asynchronous && this.hasFetchOptionsFunction) {
                    this.debouncedFetchOptions = debounce(this.debounceHandler, this.debounceDelay);
                }
                if (!this.asynchronous && this.hasFetchOptionsFunction) {
                    this.fetchOptions();
                }
            },
            debounceHandler() {
                if (this.search.length >= this.minCharToSearch) {
                    const promise = this.fetchOptionsFunction(this.getOptionsFunctionParams());
                    this.pendingFetchOptionsPromises.push(promise);
                }
            },
            getItemValue(item) {
                if (this.trackBy && typeof item === 'object') return item[this.trackBy];
                return item;
            },
            getOptionsFunctionParams() {
                return {
                    search: this.search,
                    value: this.value,
                    modelValue: this.modelValue,
                };
            },
            /**
             * Pobiera opcje asynchronicznie.
             */
            async fetchOptions() {
                try {
                    this.optionsLoading = true;
                    this.$emit('loading', true);
                    const response = await this.fetchOptionsFunction({
                        ...this.getOptionsFunctionParams(),
                        initialValue: this.initialValue,
                    });
                    this.loadedOptions = this.parseOptionsFunction ? this.parseOptionsFunction(response) : response || [];
                } catch (error) {
                    // error
                } finally {
                    this.optionsLoading = false;
                    this.$emit('loading', false);
                }

                if (this.preselectFirst) {
                    if (this.multiple) this.modelValue = [this.loadedOptions[0]];
                    else this.modelValue = this.loadedOptions[0];
                } else if (this.preselectAll) {
                    this.modelValue = this.loadedOptions;
                } else if (this.initialValue !== undefined && (Array.isArray(this.initialValue) ? !!this.initialValue.length : true)) {
                    this.initModelValue(this.initialValue);
                } else if (this.value !== undefined && (Array.isArray(this.value) ? !!this.value.length : true)) {
                    this.initModelValue(this.value);
                } else {
                    this.initModelValue();
                }

                if ((Array.isArray(this.modelValue) && this.modelValue.length) || (!Array.isArray(this.modelValue) && this.modelValue)) {
                    this.emitInput();
                    this.emitChange();
                }
            },
            /**
             * Inicjalizuje wartość modelu.
             * @param {any} value - Wartość.
             */
            initModelValue(value) {
                if (isEqual(value, this.modelValue)) {
                    return; // Nie ma potrzeby ponownej inicjalizacji
                }
                let newModelValue = null;
                const options = (this.hasFetchOptionsFunction ? (this.asynchronous && this.multiple ? this.selectedOptions : this.loadedOptions) : this.options) || [];
                if (!this.multiple) {
                    const baseValue = value && typeof value === 'object' && this.trackBy ? value[this.trackBy] : value;
                    if (typeof options[0] === 'object') {
                        if (this.trackBy) {
                            newModelValue = options.find(item => item[this.trackBy] === baseValue) || null;
                        } else {
                            newModelValue = options.find(item => item === baseValue) || null;
                        }
                    } else if (this.asynchronous && this.hasFetchOptionsFunction) {
                        newModelValue = value;
                    } else {
                        newModelValue = options.find(item => item === baseValue) || null;
                    }
                } else {
                    newModelValue = options.filter(option => this.searchOptionsFunction(option, value));
                }

                if (newModelValue) this.modelValue = newModelValue;
            },
            /**
             * Funkcja wyszukiwania opcji.
             * @param {Object} option - Opcja.
             * @param {any} value - Wartość.
             * @returns {Boolean} - Czy opcja pasuje.
             */
            searchOptionsFunction(option, value) {
                const searchFn = (i, v) => isEqual(i, v) || isEqual(String(i[this.trackBy]), String(v));
                if (this.multiple || this.showCheckboxes) return Boolean((value || []).find(v => searchFn(option, v)));
                return searchFn(option, value);
            },
            /**
             * Sprawdza, czy opcja jest zablokowana.
             * @param {Object} option - Opcja.
             * @returns {Boolean} - Czy opcja jest zablokowana.
             */
            isOptionDisabled(option) {
                if (typeof option === 'object') return !!option.$isDisabled || this.disabledIds.includes(this.getItemValue(option));
                return false;
            },
            emitInput() {
                this.$emit('input', this.modelValue, this.id);
            },
            emitChange() {
                this.emitTrackByEvent(this.modelValue, 'changeTrackBy');
                this.$emit('change', this.modelValue, this.id);
            },
        },
        created() {
            this.$on('input', this.onInputEmitted);
            this.$on('open', this.onOpenEmitted);
            this.$on('close', this.onCloseEmitted);
            if (this.preselectFirst && this.options.length) {
                this.modelValue = this.options[0];
                this.emitInput();
                this.emitChange();
            }
            if (this.preselectAll && this.options.length) {
                this.modelValue = this.options;
                this.emitInput();
                this.emitChange();
            }
            this.initAsynchronous();
        },
        beforeDestroy() {
            this.$off('input', this.onInputEmitted);
            this.$off('open', this.onOpenEmitted);
            this.$off('close', this.onCloseEmitted);
        },
    };
</script>

<style lang="scss" scoped>
    @import '../../design/index';

    .multiselect,
    .multiselect__input,
    .multiselect__single {
        font-size: $input-font-size;
    }
    .multiselect__single {
        white-space: nowrap;
        overflow: hidden;
        user-select: none;
    }

    .multiselect__placeholder {
        user-select: none;
    }

    .multiselect__option {
        width: 100%;

        &.multiselect__option {
            width: 100%;
            background: $white;
            color: $dropdown-link-color;
            padding: 0.25rem 1.25rem;
            display: flex;
            align-items: center;

            &--highlight,
            &--highlight::after {
                background: $dropdown-link-hover-bg;
                color: $dropdown-link-hover-color;
                font-weight: normal;
            }

            &--selected,
            &--selected::after {
                background: darken($dropdown-link-hover-bg, 2);
                color: darken($dropdown-link-hover-color, 2);
                font-weight: normal;
            }

            &--no-result {
                white-space: normal;
            }

            &--group {
                background: #ededed;
                color: #35495e;
            }

            &--no-option,
            &--no-result,
            &--min-char {
                cursor: default;
            }

            &-selected {
                background: $gray-200 !important;
                border-color: $gray-300 !important;
            }

            &__checkbox {
                min-height: 30px;

                &.multiselect__option--highlight,
                &.multiselect__option--selected,
                &.multiselect__option--highlight::after,
                &.multiselect__option--selected::after {
                    background: $white;
                    color: $dropdown-link-color;
                }
            }
        }
    }

    .multiselect__tags {
        position: relative;
    }

    .multiselect__tag {
        display: inline-flex;
        margin: 2px !important;
    }

    .multiselect__checkbox-wrapper {
        min-width: 100%;
        height: 100%;
        min-height: 26px;
        display: flex;
        align-items: center;
        flex-grow: 1;

        .custom-control.custom-checkbox {
            overflow: hidden;

            .custom-control-label {
                min-height: $font-size-base * $line-height-base;
                display: flex;
                align-items: center;

                span {
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    overflow: hidden;
                }
            }
        }

        .btn-select-only {
            padding: $btn-padding-y-sm $btn-padding-x-sm;
            font-size: $btn-font-size-sm;
            text-transform: uppercase;
            display: none;
            margin-left: auto;
            line-height: 1rem;
        }
    }

    .multiselect__content-container {
        min-width: 100%;
        position: relative;
        z-index: 50;
        border-bottom-left-radius: 5px;
        border-bottom-right-radius: 5px;

        .multiselect__select-all {
            width: 100%;
            background: $primary;
            padding: 5px 10px;
            margin: 0;
            color: #fff;
            border-top-left-radius: 5px;
            border-top-right-radius: 5px;
            cursor: pointer;

            &-selected {
                opacity: 0.8;
            }

            .custom-control-input {
                &:checked,
                &:indeterminate {
                    ~ .custom-control-label::before {
                        border-color: $gray-300;
                    }
                }

                &:disabled {
                    cursor: not-allowed;

                    ~ .custom-control-label {
                        cursor: not-allowed;
                    }
                }
            }
        }

        .multiselect__search-inside {
            position: relative;
            display: flex;
            padding: 0.5rem 0.75rem;
            min-height: 40px;
            border-bottom: 1px solid #e8e8e8;
            background: #fff;
            font-size: 14px;

            input {
                flex-grow: 1;
            }

            &-icon {
                width: 26px;
                height: 24px;
                flex-shrink: 0;
                margin-right: 0.75rem;
            }

            &-clear-btn {
                width: 36px;
                height: 24px;
                flex-shrink: 0;
                padding: 0;
            }
        }

        .multiselect__content-wrapper {
            width: unset;
            min-width: 100%;

            .multiselect__content {
                min-width: 100%;
                margin: 0;
                display: flex;
                flex-direction: column;
            }
        }

        .multiselect__element {
            position: relative;
            overflow: hidden;

            &:hover {
                .btn-select-only {
                    display: block;
                }
            }
        }

        &--checkboxes {
            width: 100%;
            position: absolute;
            top: 0;
            box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);

            .multiselect__content-wrapper {
                position: relative;
                border: 0;
                box-shadow: unset;
            }
        }
    }

    .multiselect--above .multiselect__content-wrapper {
        bottom: calc(100% + #{$input-height});
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.1s;
    }
    .fade-enter,
    .fade-leave-to {
        opacity: 0;
    }

    .multiselect__spinner {
        position: absolute;
        right: 1px;
        top: 50%;
        width: 34px;
        height: 34px;
        background: #fff;
        display: block;
        transform: translateY(-50%);
    }
</style>
